import H from 'history';
import { CheckUserNameIdentities, SocialProvider } from '../types';
import {
  isMicrosoftAuthEnabled,
  isAppleAuthEnabled,
  isSlackAuthEnabled,
  isGoogleLoginOutageAdvisoryEnabled,
  isMicrosoftLoginOutageAdvisoryEnabled,
  isAppleLoginOutageAdvisoryEnabled,
  isSlackLoginOutageAdvisoryEnabled,
} from '../utilities/social-login';
import { Application } from './applications';

// predicate returns true when priority should be high.
type HighPriorityPredicate = () => boolean;

export type SocialLoginEligibility = {
  isEligibleForSocialLogin: boolean;
  firstProductAccessed: Application | 'unknown';
  highlightedSocialProvider: SocialProvider | null;
  connectedSocialProviders: SocialProvider[];
  enabledSocialButtons: SocialProvider[];
  hasPassword: boolean | undefined;
};

export const defaultSocialProviderRank = [
  SocialProvider.Google,
  SocialProvider.Microsoft,
  SocialProvider.Apple,
  SocialProvider.Slack,
];

const socialProviderToHighPriorityPredicate: Record<SocialProvider, HighPriorityPredicate> = {
  [SocialProvider.Google]: () => !isGoogleLoginOutageAdvisoryEnabled(),
  [SocialProvider.Microsoft]: () => !isMicrosoftLoginOutageAdvisoryEnabled(),
  [SocialProvider.Apple]: () => !isAppleLoginOutageAdvisoryEnabled(),
  [SocialProvider.Slack]: () => !isSlackLoginOutageAdvisoryEnabled(),
};

/**
 * Ranks social providers from highest to lowest priority based on mapped predicates in socialProviderToPriorityPredicate.
 * @returns a new array containing the ordered rank of social providers. The first element is the highest rank.
 */
export const getRankedSocialProviders = () => {
  const highPriority: SocialProvider[] = [];
  const lowPriority: SocialProvider[] = [];

  defaultSocialProviderRank.forEach(provider => {
    const isHighPriority = socialProviderToHighPriorityPredicate[provider];
    if (isHighPriority && !isHighPriority()) {
      lowPriority.push(provider);
    } else {
      // No predicate means no definition of low priority, so it must be high priority.
      highPriority.push(provider);
    }
  });

  return highPriority.concat(lowPriority);
};

/**
 * Decide which social provider we should 'hero' or highlight. Full and partial outages are taken in to account.
 *
 * @param connected a list of social providers the user has connected to their atlassian account
 * @param enabled a list of social providers that are enabled by Atlassian on the login page (full outage)
 * @returns a single social provider we should nudge the user to login with.
 */
export const getHighlightedSocialProvider = (
  connected: SocialProvider[],
  enabled: SocialProvider[]
) => {
  const rankedSocialProviders = getRankedSocialProviders();
  for (const socialProvider of rankedSocialProviders) {
    if (connected.includes(socialProvider) && enabled.includes(socialProvider)) {
      return socialProvider;
    }
  }
  return null;
};

export const getConnectedSocialProviders = (
  identities: CheckUserNameIdentities | void
): SocialProvider[] => {
  if (!identities) {
    return [];
  }
  return Object.values(SocialProvider).filter(provider => !!identities?.[provider]);
};

export const getEnabledSocialButtons = (location: H.Location<any>) => {
  const enabledSocialButtons: SocialProvider[] = [SocialProvider.Google];
  if (isMicrosoftAuthEnabled(location)) {
    enabledSocialButtons.push(SocialProvider.Microsoft);
  }
  if (isAppleAuthEnabled(location)) {
    enabledSocialButtons.push(SocialProvider.Apple);
  }
  if (isSlackAuthEnabled(location)) {
    enabledSocialButtons.push(SocialProvider.Slack);
  }
  return enabledSocialButtons;
};

export const isEligibleForSocialLoginExperience = (
  socialProvider: SocialProvider | null,
  application: Application | undefined,
  hasPassword = true
) => Boolean(socialProvider && !hasPassword && application !== 'trello');

export const getSocialLoginEligibility = (
  identities: CheckUserNameIdentities | undefined,
  location: H.Location,
  application: Application | undefined
): SocialLoginEligibility => {
  const connectedSocialProviders = getConnectedSocialProviders(identities);
  const enabledSocialButtons = getEnabledSocialButtons(location);
  const highlightedSocialProvider = getHighlightedSocialProvider(
    connectedSocialProviders,
    enabledSocialButtons
  );

  const hasPassword = identities?.password;

  const isEligibleForSocialLogin = isEligibleForSocialLoginExperience(
    highlightedSocialProvider,
    application,
    hasPassword
  );

  return {
    isEligibleForSocialLogin,
    highlightedSocialProvider,
    connectedSocialProviders,
    enabledSocialButtons,
    firstProductAccessed: application || 'unknown',
    hasPassword,
  };
};
